import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { sfx } from "./citySfx";
import { img } from "./cityAssets";

const W = 900, H = 480, FLOOR = 400;
export const VILLAINS = [
  { id: "debt", name: "DEBT COLLECTOR", img: "/game/villain_debt.webp", aw: 262, ah: 363, h: 190, hp: 100, speed: 120, dmg: 7, taunt: "You owe me… a rematch.", color: "#94a3b8" },
  { id: "latefee", name: "LATE FEE", img: "/game/villain_latefee.webp", aw: 236, ah: 270, h: 150, hp: 110, speed: 170, dmg: 8, taunt: "Tick tock. 30 days late!", color: "#ef4444" },
  { id: "inquiry", name: "HARD INQUIRY", img: "/game/villain_inquiry.webp", aw: 241, ah: 339, h: 185, hp: 120, speed: 150, dmg: 9, taunt: "Just a quick look… -5 points.", color: "#a855f7" },
  { id: "util", name: "HIGH UTILIZATION", img: "/game/villain_util.webp", aw: 338, ah: 343, h: 200, hp: 150, speed: 100, dmg: 12, taunt: "Maxed out and proud!", color: "#f59e0b" },
];
const DUCK = { aw: 167, ah: 250, h: 190 };
const PUNCH = { windup: 0.08, active: 0.12, recover: 0.16, range: 70, dmg: 9 };
const KICK = { windup: 0.14, active: 0.14, recover: 0.24, range: 95, dmg: 14 };

function mkFighter(x, facing, hp, spec) { return { x, y: FLOOR, vy: 0, facing, hp, maxHp: hp, atk: null, atkT: 0, hitT: 0, block: false, cd: 0, spec }; }

export function DuckFighter({ onClose, onKo }) {
  const ref = useRef(null);
  const [ui, setUi] = useState({ state: "intro", stage: 0, msg: "" });
  const g = useRef(null);

  useEffect(() => {
    const canvas = ref.current; const ctx = canvas.getContext("2d");
    const keys = {};
    const state = { phase: "intro", stage: 0, timer: 60, p: null, e: null, msgT: 0, msg: "", shake: 0, fx: [], last: 0, hits: 0 };
    g.current = state;
    const startRound = (stage) => {
      const v = VILLAINS[stage];
      state.p = mkFighter(220, 1, 100, DUCK); state.e = mkFighter(680, -1, v.hp, v);
      state.timer = 60; state.phase = "fight"; state.msg = "ROUND 1 · FIGHT!"; state.msgT = 1.2; state.stage = stage;
      setUi({ state: "fight", stage, msg: v.taunt });
      sfx.whoosh();
    };
    state.start = startRound;
    const kd = (e) => { keys[e.key] = true; if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", " ", "z", "x", "Z", "X"].includes(e.key)) e.preventDefault(); if (state.phase !== "fight" && (e.key === "Enter" || e.key === " ")) advance(); };
    const ku = (e) => { delete keys[e.key]; };
    const advance = () => {
      if (state.phase === "intro") startRound(0);
      else if (state.phase === "won") { if (state.stage + 1 < VILLAINS.length) startRound(state.stage + 1); else { state.phase = "champion"; setUi({ state: "champion", stage: state.stage, msg: "" }); sfx.win(); } }
      else if (state.phase === "lost") startRound(state.stage);
      else if (state.phase === "champion") startRound(0);
    };
    state.advance = advance;
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    const btn = (k) => keys[k] || state.touch?.[k];
    const attack = (f, kind) => { if (f.atk || f.hitT > 0) return; f.atk = kind; f.atkT = 0; f.block = false; sfx.whoosh(); };
    const attackBox = (f) => {
      const spec = f.atk === "kick" ? KICK : PUNCH; if (!f.atk || f.atkT < spec.windup || f.atkT > spec.windup + spec.active) return null;
      return { x: f.facing > 0 ? f.x + 20 : f.x - 20 - spec.range, w: spec.range, y: f.y - f.spec.h * 0.75, h: f.spec.h * 0.5, dmg: spec.dmg };
    };
    const resolve = (a, d) => {
      const box = attackBox(a); if (!box || a.hitDone) return;
      const dw = d.spec.h * 0.3;
      if (box.x < d.x + dw && box.x + box.w > d.x - dw && d.y > box.y) {
        a.hitDone = true;
        const dmg = d.block ? Math.round(box.dmg * 0.25) : box.dmg;
        d.hp = Math.max(0, d.hp - dmg); d.hitT = d.block ? 0.12 : 0.32; d.x += a.facing * (d.block ? 14 : 34); state.shake = d.block ? 3 : 8;
        state.fx.push({ x: d.x, y: d.y - d.spec.h * 0.7, t: 0, txt: d.block ? "BLOCK" : `-${dmg}`, c: d.block ? "#93c5fd" : "#fde047" });
        sfx.hit();
      }
    };
    const stepFighter = (f, dt) => {
      f.vy += 1500 * dt; f.y = Math.min(FLOOR, f.y + f.vy * dt); if (f.y >= FLOOR) f.vy = 0;
      f.x = Math.max(60, Math.min(W - 60, f.x));
      if (f.atk) { const spec = f.atk === "kick" ? KICK : PUNCH; f.atkT += dt; if (f.atkT > spec.windup + spec.active + spec.recover) { f.atk = null; f.hitDone = false; } }
      if (f.hitT > 0) f.hitT -= dt; if (f.cd > 0) f.cd -= dt;
    };
    const loop = (now) => {
      const dt = Math.min(0.04, (now - (state.last || now)) / 1000); state.last = now;
      const t = now / 1000;
      if (state.phase === "fight") {
        const { p, e } = state;
        state.timer -= dt;
        p.facing = e.x > p.x ? 1 : -1; e.facing = p.x > e.x ? 1 : -1;
        p.block = false;
        if (p.hitT <= 0 && !p.atk) {
          if (btn("ArrowLeft") || btn("a")) p.x -= 230 * dt; if (btn("ArrowRight") || btn("d")) p.x += 230 * dt;
          if ((btn("ArrowUp") || btn("w")) && p.y >= FLOOR) { p.vy = -620; sfx.whoosh(); }
          if (btn("ArrowDown") || btn("s")) p.block = true;
          if (btn("z") || btn("Z") || btn("j")) attack(p, "punch"); if (btn("x") || btn("X") || btn("k")) attack(p, "kick");
        }
        const dist = Math.abs(e.x - p.x);
        if (e.hitT <= 0 && !e.atk) {
          const wantRange = e.spec.id === "inquiry" ? 105 : 88;
          if (dist > wantRange + 10) e.x += e.facing * e.spec.speed * dt;
          else if (dist < wantRange - 30 && Math.random() < 0.02) e.x -= e.facing * e.spec.speed * dt * 6;
          e.block = p.atk && Math.random() < (0.25 + state.stage * 0.12) && e.cd <= 0;
          if (dist <= wantRange + 15 && e.cd <= 0 && Math.random() < 0.05 + state.stage * 0.015) { attack(e, Math.random() < 0.6 ? "punch" : "kick"); e.cd = 0.5 - state.stage * 0.06; }
          if (e.y >= FLOOR && Math.random() < 0.004) e.vy = -560;
        }
        stepFighter(p, dt); stepFighter(e, dt);
        const sep = 78, gap = Math.abs(e.x - p.x);
        if (gap < sep) { const push = (sep - gap) / 2, dir = e.x >= p.x ? 1 : -1; p.x -= dir * push; e.x += dir * push; }
        resolve(p, e); resolve(e, p);
        if (e.hp <= 0) { state.phase = "won"; state.msg = "K.O.!"; sfx.win(); onKo?.(); setUi({ state: "won", stage: state.stage, msg: `${e.spec.name} is down!` }); }
        else if (p.hp <= 0) { state.phase = "lost"; state.msg = "K.O."; sfx.ko(); setUi({ state: "lost", stage: state.stage, msg: `${e.spec.name} got you. Try again.` }); }
        else if (state.timer <= 0) { const win = p.hp >= e.hp; state.phase = win ? "won" : "lost"; state.msg = "TIME"; setUi({ state: win ? "won" : "lost", stage: state.stage, msg: win ? "Time! You win on health." : "Time! Villain wins on health." }); }
      }
      if (state.msgT > 0) state.msgT -= dt; if (state.shake > 0) state.shake = Math.max(0, state.shake - dt * 40);
      state.fx = state.fx.filter((f) => (f.t += dt) < 0.8);
      draw(ctx, state, t);
      state.raf = requestAnimationFrame(loop);
    };
    state.raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(state.raf); window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  const touch = (k, on) => { const s = g.current; if (!s) return; s.touch = s.touch || {}; s.touch[k] = on; };
  const TB = ({ k, label, testid }) => (
    <button className="cq-px text-[9px] h-12 w-14 bg-slate-800/90 text-white border border-slate-500 active:bg-cyan-500 select-none touch-none" data-testid={testid}
      onPointerDown={(e) => { e.preventDefault(); touch(k, true); }} onPointerUp={() => touch(k, false)} onPointerLeave={() => touch(k, false)} onPointerCancel={() => touch(k, false)}>{label}</button>
  );

  return (
    <div className="relative w-full" data-testid="duck-fighter">
      <canvas ref={ref} width={W} height={H} className="w-full h-auto block border-2 border-pink-500/60 shadow-[0_0_40px_rgba(236,72,153,0.35)] bg-[#0b0a1f]" data-testid="fighter-canvas" onClick={() => { const s = g.current; if (s && s.phase !== "fight") s.advance(); }} />
      {ui.state !== "fight" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-black/55 pointer-events-none">
          <p className="cq-px text-pink-400 text-lg sm:text-3xl cq-neon-pink" data-testid="fighter-title">
            {ui.state === "intro" ? "CREDIT BRAWL" : ui.state === "won" ? "K.O.!" : ui.state === "lost" ? "YOU LOST" : "CHAMPION!"}
          </p>
          <p className="cq-px text-[9px] sm:text-xs text-slate-200 mt-4 leading-loose max-w-lg">
            {ui.state === "intro" ? "4 Credit Villains stand between you and the belt. ← → move · ↑ jump · ↓ block · Z punch · X kick" : ui.state === "champion" ? "All four villains are down. Your credit is officially undefeated." : ui.msg}
          </p>
          <button className="cq-px pointer-events-auto mt-6 text-[10px] sm:text-xs border-2 border-amber-300 text-amber-300 bg-[#1a1440] px-6 py-3 cq-blink" onClick={() => g.current?.advance()} data-testid="fighter-advance">
            {ui.state === "intro" ? "▶ FIGHT" : ui.state === "won" ? (ui.stage + 1 < VILLAINS.length ? "NEXT VILLAIN ▶" : "CLAIM THE BELT") : ui.state === "lost" ? "REMATCH" : "PLAY AGAIN"}
          </button>
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 sm:hidden">
        <div className="flex gap-1"><TB k="ArrowLeft" label="◀" testid="fb-left" /><TB k="ArrowRight" label="▶" testid="fb-right" /></div>
        <div className="flex gap-1"><TB k="ArrowUp" label="JMP" testid="fb-jump" /><TB k="ArrowDown" label="BLK" testid="fb-block" /><TB k="z" label="PUN" testid="fb-punch" /><TB k="x" label="KIK" testid="fb-kick" /></div>
      </div>
      <p className="cq-px text-[7px] text-slate-400 mt-3 text-center hidden sm:block">← → move · ↑ jump · ↓ block · Z punch · X kick · Enter to continue</p>
    </div>
  );
}

function drawFighter(ctx, f, t, isDuck) {
  const h = f.spec.h, w = (h * f.spec.aw) / f.spec.ah;
  const spec = f.atk === "kick" ? KICK : PUNCH;
  const lunge = f.atk && f.atkT > spec.windup ? f.facing * (f.atk === "kick" ? 26 : 16) : 0;
  const crouch = f.block ? 0.9 : 1;
  ctx.save();
  ctx.translate(f.x + lunge, f.y);
  if (f.hitT > 0 && Math.floor(t * 30) % 2) ctx.globalAlpha = 0.55;
  ctx.scale(f.facing * (isDuck ? 1 : -1), crouch);
  const im = img(isDuck ? "/game/duck_walk_0.webp" : f.spec.img);
  if (im) ctx.drawImage(im, -w / 2, -h, w, h); else { ctx.fillStyle = f.spec.color || "#fff"; ctx.fillRect(-w / 2, -h, w, h); }
  if (f.atk && f.atkT > spec.windup && f.atkT < spec.windup + spec.active) {
    ctx.fillStyle = isDuck ? "#fde047" : f.spec.color; ctx.globalAlpha = 0.9;
    const ax = w / 2 - 6, ay = f.atk === "kick" ? -h * 0.32 : -h * 0.6;
    ctx.beginPath(); ctx.ellipse(ax + spec.range * 0.5, ay, spec.range * 0.5, 14, 0, 0, 7); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(ax + spec.range * 0.8, ay, 9, 0, 7); ctx.fill();
  }
  if (f.block) { ctx.strokeStyle = "#93c5fd"; ctx.lineWidth = 4; ctx.globalAlpha = 0.8; ctx.beginPath(); ctx.arc(0, -h * 0.5, h * 0.55, -1.3, 1.3); ctx.stroke(); }
  ctx.restore();
}

function bar(ctx, x, y, w, v, max, right, color) {
  ctx.fillStyle = "#0f172a"; ctx.fillRect(x - 3, y - 3, w + 6, 24);
  ctx.fillStyle = "#7f1d1d"; ctx.fillRect(x, y, w, 18);
  const fw = (w * Math.max(0, v)) / max;
  ctx.fillStyle = color; ctx.fillRect(right ? x + w - fw : x, y, fw, 18);
}

function draw(ctx, s, t) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  const sh = s.shake ? (Math.random() - 0.5) * s.shake : 0;
  ctx.translate(sh, sh * 0.5);
  const bg = img("/game/room_dojo.webp");
  const bh = W / (1264 / 848);
  if (bg) ctx.drawImage(bg, 0, FLOOR - bh * 0.8, W, bh); else { ctx.fillStyle = "#1a1030"; ctx.fillRect(0, 0, W, H); }
  ctx.fillStyle = "rgba(0,0,0,0.22)"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "rgba(0,0,0,0.35)"; ctx.fillRect(0, FLOOR + 2, W, 3);
  if (!s.p) return;
  [s.p, s.e].sort((a, b) => (a.atk ? 1 : 0) - (b.atk ? 1 : 0)).forEach((f) => { ctx.fillStyle = "rgba(0,0,0,0.4)"; ctx.beginPath(); ctx.ellipse(f.x, FLOOR + 4, f.spec.h * 0.28, 8, 0, 0, 7); ctx.fill(); drawFighter(ctx, f, t, f === s.p); });
  ctx.font = '11px "Press Start 2P", monospace'; ctx.textBaseline = "middle";
  bar(ctx, 30, 30, 330, s.p.hp, s.p.maxHp, false, "#22d3ee"); bar(ctx, W - 360, 30, 330, s.e.hp, s.e.maxHp, true, s.e.spec.color);
  ctx.fillStyle = "#fff"; ctx.textAlign = "left"; ctx.fillText("DUCKY", 30, 66); ctx.textAlign = "right"; ctx.fillText(s.e.spec.name, W - 30, 66);
  ctx.fillStyle = "rgba(0,0,0,0.45)"; ctx.fillRect(0, 0, W, 84);
  bar(ctx, 30, 30, 330, s.p.hp, s.p.maxHp, false, "#22d3ee"); bar(ctx, W - 360, 30, 330, s.e.hp, s.e.maxHp, true, s.e.spec.color);
  ctx.fillStyle = "#fff"; ctx.textAlign = "left"; ctx.fillText("DUCKY", 30, 66); ctx.textAlign = "right"; ctx.fillText(s.e.spec.name, W - 30, 66);
  ctx.textAlign = "center"; ctx.font = '20px "Press Start 2P", monospace'; ctx.fillStyle = s.timer < 10 ? "#f87171" : "#fde047"; ctx.fillText(String(Math.max(0, Math.ceil(s.timer))), W / 2, 42);
  ctx.font = '8px "Press Start 2P", monospace'; ctx.fillStyle = "#a5b4fc"; ctx.fillText(`VILLAIN ${s.stage + 1} / ${VILLAINS.length}`, W / 2, 70);
  for (const f of s.fx) { ctx.globalAlpha = 1 - f.t / 0.8; ctx.font = '16px "Press Start 2P", monospace'; ctx.fillStyle = f.c; ctx.fillText(f.txt, f.x, f.y - f.t * 60); ctx.globalAlpha = 1; }
  if (s.msgT > 0) { ctx.font = '28px "Press Start 2P", monospace'; ctx.fillStyle = "#fde047"; ctx.strokeStyle = "#7c2d12"; ctx.lineWidth = 4; ctx.strokeText(s.msg, W / 2, H / 2 - 40); ctx.fillText(s.msg, W / 2, H / 2 - 40); }
}

export default function DuckFighterModal({ open, onClose, onKo }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-3" role="dialog" aria-modal="true" data-testid="fighter-modal">
      <div className="relative w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <button onClick={onClose} className="absolute -top-1 right-0 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" aria-label="Close" data-testid="fighter-modal-close"><X size={18} /></button>
        <p className="cq-px text-[9px] text-pink-300 mb-2">DUCK DOJO · CREDIT BRAWL</p>
        <DuckFighter onClose={onClose} onKo={onKo} />
      </div>
    </div>
  );
}
